import { useState, type ReactNode } from 'react';
import { X, CircleNotch, CheckCircle, ArrowsLeftRight, Sparkle, WarningCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { proposalService } from '../../../services/proposal.service';
import { docName } from '../proposalDocName';
import type {
  DocumentUpdateMode, Proposal, ProposalChange, ProposalChangeValue, ProposalHighlights, ProposalInput,
  ProposalMilestone,
} from '../../../services/proposal.service';

interface ProposalCompareModalProps {
  /** analysing: the AI is still reading the file (the window opens at once so the user sees it working). */
  status: 'analysing' | 'ready' | 'nothing' | 'error';
  message?: string;
  proposal: Proposal;
  /** Replace only: the replaced file (can be kept instead of the new one) and the new file. */
  oldKey?: string;
  newKey?: string;
  changes: ProposalChange[];
  mode: DocumentUpdateMode;
  fileNames: string[];
  onClose: () => void;
  onSaved: (p: Proposal) => void;
}

type Choice = 'keep' | 'new';

const EMPTY_HIGHLIGHTS: ProposalHighlights = { problem: '', solution: '', impact: '', milestones: [] };

const isMilestones = (v: unknown[]): v is ProposalMilestone[] => typeof v[0] === 'object' && v[0] !== null;

function Value({ field, value }: { field: string; value: ProposalChangeValue }) {
  if (value == null || value === '' || (Array.isArray(value) && value.length === 0)) {
    return <span className="text-[11px] italic text-slate-400">empty</span>;
  }
  if (Array.isArray(value)) {
    if (isMilestones(value)) {
      return (
        <ul className="space-y-1">
          {value.map((m, i) => (
            <li key={i} className="text-xs text-slate-700 leading-snug">
              {m.period && <span className="font-bold">{m.period}: </span>}
              {m.text}
            </li>
          ))}
        </ul>
      );
    }
    return (
      <div className="flex flex-wrap gap-1">
        {value.map((v, i) => (
          <span key={i} className="text-[10px] font-bold bg-slate-100 border border-slate-200 text-slate-600 px-2 py-0.5 rounded-lg">{v}</span>
        ))}
      </div>
    );
  }
  const text = field === 'budget_lakhs' ? `₹${value} Lakhs` : field === 'duration_months' ? `${value} months` : String(value);
  return <p className="text-xs text-slate-700 leading-relaxed whitespace-pre-wrap">{text}</p>;
}

function Side({ title, selected, onClick, children }: {
  title: string; selected: boolean; onClick: () => void; children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left rounded-xl border p-3 transition-all ${
        selected ? 'border-accent bg-accent/5 ring-1 ring-accent' : 'border-slate-200 bg-white hover:bg-slate-50'
      }`}
    >
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400">{title}</span>
        {selected && <CheckCircle size={14} weight="fill" className="text-accent" />}
      </div>
      {children}
    </button>
  );
}

function StatusBody({ status, message, fileNames }: {
  status: 'analysing' | 'nothing' | 'error'; message?: string; fileNames: string[];
}) {
  return (
    <div className="p-10 flex flex-col items-center justify-center text-center gap-3 flex-1">
      {status === 'analysing' && (
        <>
          <CircleNotch className="w-8 h-8 text-accent animate-spin" />
          <p className="text-sm font-bold text-slate-700">Analysing {fileNames.join(', ')} and detecting changes…</p>
          <p className="text-xs text-slate-500 max-w-md">
            The AI is reading the new document and comparing it with the current values. This usually takes 15–60 seconds.
          </p>
        </>
      )}
      {status === 'nothing' && (
        <>
          <CheckCircle className="w-8 h-8 text-emerald-500" weight="fill" />
          <p className="text-sm font-bold text-slate-700">No changes found</p>
          <p className="text-xs text-slate-500 max-w-md">The new document matches the current values, so nothing needs updating.</p>
        </>
      )}
      {status === 'error' && (
        <>
          <WarningCircle className="w-8 h-8 text-amber-500" weight="fill" />
          <p className="text-sm font-bold text-slate-700">Could not compare</p>
          <p className="text-xs text-slate-500 max-w-md">{message || 'The AI could not read the document. No fields were changed.'}</p>
        </>
      )}
    </div>
  );
}

/** Current values on the left, the AI's values from the new document on the right; one PUT with what was accepted. */
export function ProposalCompareModal({
  status, message, proposal, oldKey, newKey, changes, mode, fileNames, onClose, onSaved,
}: ProposalCompareModalProps) {
  const [choice, setChoice] = useState<Record<string, Choice>>(
    () => Object.fromEntries(changes.map((c) => [c.field, 'new' as Choice])),
  );
  const [saving, setSaving] = useState(false);
  // Which file is the main document after a replace; the new one is already in place.
  const [fileChoice, setFileChoice] = useState<Choice>('new');
  const canChooseFile = mode === 'replace' && !!oldKey && !!newKey && status !== 'analysing';
  const keepOldFile = canChooseFile && fileChoice === 'keep';
  const accepted = status === 'ready' ? changes.filter((c) => choice[c.field] === 'new') : [];
  const pending = accepted.length + (keepOldFile ? 1 : 0);
  const setAll = (v: Choice) => setChoice(Object.fromEntries(changes.map((c) => [c.field, v])));

  const save = async () => {
    const pid = proposal.proposal_id as string;
    setSaving(true);
    try {
      let latest = proposal;
      if (keepOldFile) {
        latest = (await proposalService.restoreDocument(pid, oldKey as string)).proposal;
      }
      if (accepted.length > 0) latest = await saveFields(latest);
      toast.success(keepOldFile && accepted.length === 0 ? `${pid}: kept the old file` : `${pid} updated`);
      onSaved(latest);
      onClose();
    } catch (err) {
      // 422 when the saved proposal itself lacks a required field (some imported rows do) and it was kept.
      const e = err as { message?: string; data?: { detail?: { missing?: string[] } } } | null;
      const missing = e?.data?.detail?.missing;
      toast.error(missing?.length
        ? `This proposal is missing required fields (${missing.join(', ')}). Use the new values for them, or fill them via Edit.`
        : e?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const saveFields = async (current: Proposal): Promise<Proposal> => {
    const body: Record<string, unknown> = {};
    let highlights: ProposalHighlights | null = null;
    for (const c of accepted) {
      if (c.field.startsWith('highlights.')) {
        const part = c.field.slice('highlights.'.length) as keyof ProposalHighlights;
        highlights = { ...(highlights || current.highlights || EMPTY_HIGHLIGHTS), [part]: c.suggested } as ProposalHighlights;
      } else {
        body[c.field] = c.suggested;
      }
    }
    if (highlights) body.highlights = highlights;
    const saved = await proposalService.update(current.proposal_id as string, body as ProposalInput);
    if (saved.index_pending) toast.warning('Saved, but the search index could not be updated yet.');
    return saved.proposal;
  };

  const fileRow = canChooseFile && (
    <div className="px-6 py-4 border-b border-slate-100 space-y-1.5 shrink-0">
      <span className="text-[11px] font-bold text-slate-700 uppercase tracking-wider">Main document</span>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Side title="New file — use" selected={fileChoice === 'new'} onClick={() => setFileChoice('new')}>
          <p className="text-xs text-slate-700 font-semibold truncate">{docName(newKey as string)}</p>
        </Side>
        <Side title="Old file — keep" selected={fileChoice === 'keep'} onClick={() => setFileChoice('keep')}>
          <p className="text-xs text-slate-700 font-semibold truncate">{docName(oldKey as string)}</p>
          <p className="text-[10px] text-slate-400">The new file is kept under "Replaced" as not used.</p>
        </Side>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-xs animate-in fade-in duration-200">
      <div className="bg-white border border-slate-200/80 rounded-3xl shadow-2xl w-full max-w-4xl overflow-hidden flex flex-col max-h-[92vh]">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50 shrink-0">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 rounded-xl bg-accent/10 flex items-center justify-center text-accent shrink-0">
              <ArrowsLeftRight className="w-4 h-4" weight="bold" />
            </div>
            <div className="min-w-0">
              <h3 className="font-bold text-slate-800 text-sm truncate">
                Review changes for {proposal.proposal_id} — {mode === 'add' ? 'added document' : 'replaced document'}
              </h3>
              <p className="text-[11px] text-slate-500 font-medium truncate">
                From {fileNames.join(', ')}. The document is already saved{status === 'ready' ? '; choose which values to update.' : '.'}
              </p>
            </div>
          </div>
          <button onClick={onClose} disabled={saving} className="text-slate-400 hover:text-slate-600 transition-colors p-1.5 rounded-full hover:bg-slate-100">
            <X className="w-4 h-4" />
          </button>
        </div>

        {fileRow}
        {status !== 'ready' ? (
          <StatusBody status={status} message={message} fileNames={fileNames} />
        ) : (<>
        <div className="px-6 py-3 border-b border-slate-100 flex items-center justify-between gap-3 shrink-0">
          <span className="text-[11px] font-semibold text-slate-500">
            {changes.length} field{changes.length > 1 ? 's' : ''} differ
          </span>
          <div className="flex gap-2">
            <button type="button" onClick={() => setAll('keep')} disabled={saving}
              className="px-3 py-1.5 rounded-xl border border-slate-200 text-slate-600 font-bold text-[11px] hover:bg-slate-50 transition-all">
              Keep all current
            </button>
            <button type="button" onClick={() => setAll('new')} disabled={saving}
              className="px-3 py-1.5 rounded-xl border border-accent/30 text-accent font-bold text-[11px] hover:bg-accent/5 transition-all">
              Accept all new
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          {changes.map((c) => (
            <div key={c.field} className="space-y-1.5">
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-bold text-slate-700 uppercase tracking-wider">{c.label}</span>
                {c.is_new && (
                  <span className="text-[9px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                    New — not in list
                  </span>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Side title="Current — keep" selected={choice[c.field] === 'keep'}
                  onClick={() => setChoice((s) => ({ ...s, [c.field]: 'keep' }))}>
                  <Value field={c.field} value={c.current} />
                </Side>
                <Side title="New from document — use" selected={choice[c.field] === 'new'}
                  onClick={() => setChoice((s) => ({ ...s, [c.field]: 'new' }))}>
                  <Value field={c.field} value={c.suggested} />
                </Side>
              </div>
            </div>
          ))}
        </div>

        </>)}

        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 bg-slate-50/50 shrink-0">
          {status !== 'ready' ? (
            <>
              <span className="text-[11px] text-slate-400 font-medium">
                {status === 'analysing' ? 'You can close this; the result opens here when it is ready.' : ''}
              </span>
              <div className="flex gap-2">
                <button type="button" onClick={onClose} disabled={saving}
                  className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 font-semibold text-xs hover:bg-slate-50 transition-all">
                  Close
                </button>
                {keepOldFile && (
                  <button type="button" onClick={save} disabled={saving}
                    className="flex items-center gap-2 px-5 py-2 rounded-xl bg-accent text-white font-bold text-xs shadow-md shadow-accent/20 hover:bg-accent/90 disabled:opacity-50 transition-all">
                    {saving ? <CircleNotch className="w-4 h-4 animate-spin" /> : null} Keep old file
                  </button>
                )}
              </div>
            </>
          ) : (<>
          <button type="button" onClick={onClose} disabled={saving}
            className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 font-semibold text-xs hover:bg-slate-50 transition-all">
            Close without changes
          </button>
          <button type="button" onClick={save} disabled={saving || pending === 0}
            className="flex items-center gap-2 px-5 py-2 rounded-xl bg-accent text-white font-bold text-xs shadow-md shadow-accent/20 hover:bg-accent/90 disabled:opacity-50 transition-all">
            {saving ? (<><CircleNotch className="w-4 h-4 animate-spin" /> Saving…</>)
              : (<><Sparkle className="w-4 h-4" weight="fill" /> Save {pending} change{pending === 1 ? '' : 's'}</>)}
          </button>
          </>)}
        </div>
      </div>
    </div>
  );
}
